import { Link } from "react-router-dom";
import { ArrowUpRight, HeartHandshake, CheckCircle2 } from "lucide-react";
import { GENERAL_CARE, FAQS } from "@/data/site";
import { Reveal, StaggerGroup, StaggerItem } from "@/components/motion/Reveal";
import Icon from "@/components/Icon";
import PageHero from "@/components/PageHero";
import FAQSection from "@/components/FAQSection";
import CTASection from "@/components/CTASection";

const WHO_SHOULD = [
  "Anyone with fever, cough, cold, flu or seasonal infection",
  "Adults managing diabetes, blood pressure or thyroid disorders",
  "People with digestive discomfort, headaches or unexplained fatigue",
  "Individuals seeking a preventive health check-up",
  "Families needing dependable, ongoing everyday health care",
];

export default function GeneralCare() {
  return (
    <>
      <PageHero
        crumb="General Care"
        eyebrow="General Physician Care"
        titleLines={["Beyond Cardiology —", "Everyday Health Care", "in Varanasi"]}
        subtitle="Dr. Pallavi Mishra offers dependable General Physician care for common everyday health concerns — from fever and infections to diabetes, blood pressure, thyroid and preventive check-ups."
      />

      {/* Intro band */}
      <section className="pb-20 bg-paper" data-testid="general-intro">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">One Doctor, Whole-Person Care</p>
              <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink font-light text-balance">
                A trusted first point of care for the whole family.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <Reveal delay={0.1}>
              <p className="text-lg text-sage leading-relaxed">
                Not every visit needs a specialist first. Dr. Pallavi Mishra combines her cardiology expertise with strong General Physician practice, so common health concerns — from a stubborn fever to long-term diabetes or blood pressure — can be evaluated, managed and, when needed, referred with a clear plan.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Conditions grid */}
      <section className="py-20 md:py-24 bg-surface border-y border-line" data-testid="general-conditions">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Conditions We Care For</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight text-ink font-light max-w-3xl mb-14">
              Common concerns, expertly handled.
            </h2>
          </Reveal>
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line rounded-3xl overflow-hidden">
            {GENERAL_CARE.map((c, i) => (
              <StaggerItem key={c.id} data-testid={`general-condition-${c.id}`}>
                <div id={c.id} className="group bg-paper p-8 h-full transition-colors duration-300 hover:bg-mint/40 scroll-mt-32">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-mint text-brand mb-6 transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                    <Icon name={c.icon} size={24} />
                  </span>
                  <div className="text-xs font-semibold text-brand/70 mb-2">{String(i + 1).padStart(2, "0")}</div>
                  <h3 className="font-display text-2xl text-ink mb-3">{c.title}</h3>
                  <p className="text-sm text-sage leading-relaxed">{c.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Who should consult */}
      <section className="py-24 md:py-32 bg-brand-deep text-white grain relative overflow-hidden" data-testid="general-who">
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">Who Should Consult</p>
              <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tight font-light text-balance">
                Everyday health, taken seriously.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <StaggerGroup className="divide-y divide-white/10 border-t border-white/10">
              {WHO_SHOULD.map((item, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-6 py-6">
                    <span className="font-display text-2xl text-brand w-10 shrink-0">0{i + 1}</span>
                    <p className="text-lg md:text-xl text-mint/85 leading-snug">{item}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="py-24 md:py-32 bg-paper" data-testid="general-expect">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-brand mb-5">What to Expect</p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-ink font-light max-w-3xl mb-12">
              Simple, thorough, unhurried.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { t: "Full history & assessment", d: "A careful conversation about your symptoms, medical history and current medications." },
              { t: "Focused examination & tests", d: "A physical examination and only the investigations that will genuinely inform your care." },
              { t: "Clear plan & follow-up", d: "A plain-language plan, prescription where needed, and a clear next step — including specialist referral if required." },
            ].map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="rounded-3xl border border-line bg-surface p-8 h-full">
                  <span className="font-display text-4xl text-brand/40">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-2xl text-ink mt-3 mb-3">{s.t}</h3>
                  <p className="text-sm text-sage leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-10 flex items-start gap-4 rounded-3xl bg-mint border border-line p-8">
              <HeartHandshake size={26} className="text-brand shrink-0 mt-1" />
              <div>
                <h4 className="font-display text-2xl text-ink mb-2">Coordinated with Your Heart Health</h4>
                <p className="text-sage leading-relaxed max-w-3xl">Because Dr. Mishra is a cardiologist, your general care is always considered in the context of your long-term heart health — with Dr. Ashutosh Mishra available for integrated metabolic & endocrine care.</p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <Link
              to="/services"
              className="mt-10 inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-brand hover:text-brand-dark transition-colors"
            >
              See all cardiology services <ArrowUpRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      <FAQSection items={FAQS.general_care} heading="General Care — FAQs" />
      <CTASection title="Book a General Care Consultation" />
    </>
  );
}
